import { useState, useEffect, useRef } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Spinner from "@/v2-ui/spinner"
import ErrorMessage from "@/v2-ui/ErrorMessage"

export type EmbedProps = {
  url: string
  /** Hash params to append to the iframe src (e.g. "filters=..."). Resolved server-side through redirects. */
  iframeHash?: string
  className?: string
}

type EmbedResponse = {
  html: string
  width?: number
  height?: number
}

const cache = new Map<string, EmbedResponse>()

/**
 * Fetches Embed HTML from the backend and renders it.
 * Caches responses so tab switches don't re-fetch.
 */
function Embed(props: EmbedProps) {
  const { url, iframeHash, className } = props

  const cacheKey = iframeHash ? `${url}::${iframeHash}` : url

  const [response, setResponse] = useState<EmbedResponse | null>(
    cache.get(cacheKey) ?? null,
  )
  const [error, setError] = useState<Error | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (cache.has(cacheKey)) {
      setResponse(cache.get(cacheKey)!)
      return
    }

    setError(null)

    let fetchUrl = `/embed?url=${encodeURIComponent(url)}`
    if (iframeHash) {
      fetchUrl += `&iframeHash=${encodeURIComponent(iframeHash)}`
    }

    fetch(fetchUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load embed (${res.status})`)
        return res.json() as Promise<EmbedResponse>
      })
      .then((data) => {
        cache.set(cacheKey, data)
        setResponse(data)
      })
      .catch(setError)
  }, [cacheKey, url, iframeHash])

  useEffect(() => {
    if (!containerRef.current || !response) return
    const iframe = containerRef.current.querySelector("iframe")
    if (!iframe) return
    iframe.style.width = "100%"
    iframe.style.height = "100%"
    iframe.style.border = "none"
  }, [response])

  if (error) {
    return (
      <div className="embed-placeholder">
        <ErrorMessage error={error} />
      </div>
    )
  }

  if (!response) {
    return (
      <div className="embed-placeholder">
        <Spinner displaySize="md">Loading dashboard...</Spinner>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn("embed", className)}
      dangerouslySetInnerHTML={{ __html: response.html }}
    />
  )
}

export default Embed
